/**
 * Shared text formatting utilities for consistent UX across the platform
 */

// Common department acronyms that should always be uppercase
const DEPARTMENT_ACRONYMS = [
  'hr',     // Human Resources
  'it',     // Information Technology  
  'pm',     // Project Management
  'qa',     // Quality Assurance
  'qc',     // Quality Control
  'ui',     // User Interface
  'ux',     // User Experience
  'ai',     // Artificial Intelligence
  'ml',     // Machine Learning
  'api',    // Application Programming Interface
  'cto',    // Chief Technology Officer
  'ceo',    // Chief Executive Officer
  'cfo',    // Chief Financial Officer
  'coo',    // Chief Operating Officer
  'vp',     // Vice President
  'r&d',    // Research and Development
  'rd',     // Research and Development (alternative)
  'pr',     // Public Relations
  'seo',    // Search Engine Optimization
  'crm',    // Customer Relationship Management
  'erp',    // Enterprise Resource Planning
  'bi',     // Business Intelligence
  'devops', // Development Operations
  'ops',    // Operations
  'ba',     // Business Analyst
  'dba',    // Database Administrator
  'admin',  // Administration
  'support', // Customer Support
  'sales',  // Sales (though not an acronym, often styled as SALES in some contexts)
];

/**
 * Formats category names, employment types, and other similar text fields
 * Converts strings like "software-engineer" or "full_time" to "Software Engineer" or "Full Time"
 * Special handling for department acronyms: "hr" -> "HR", "it" -> "IT", "pm" -> "PM", etc.
 */
export const formatDisplayText = (text: string): string => {
  if (!text) return '';
  return text
    .split(/[-_\s]+/) // Split on hyphens, underscores, or spaces
    .map(word => {
      const lowerWord = word.toLowerCase();
      
      // Check if this word is a department acronym
      if (DEPARTMENT_ACRONYMS.includes(lowerWord)) {
        return word.toUpperCase();
      }
      
      // Regular title case formatting
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Generates company initials from company name
 * "Apple Inc" -> "AI", "Google LLC" -> "GL"
 */
export const getCompanyInitials = (companyName: string): string => {
  if (!companyName) return '';
  return companyName
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

/**
 * Generates user initials from full name
 * "John Smith" -> "JS", "Mary Jane Watson" -> "MJ"
 */
export const getUserInitials = (fullName: string): string => {
  if (!fullName) return '';
  return fullName
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
};

/**
 * Formats job categories for display
 * @deprecated Use formatDisplayText instead
 */
export const formatCategoryName = formatDisplayText;

/**
 * Formats employment types for display  
 * @deprecated Use formatDisplayText instead
 */
export const formatEmploymentType = formatDisplayText;

/**
 * Formats reward amounts with proper number formatting (e.g., "7,500")
 * Always removes decimal places and adds thousand separators
 */
export const formatRewardAmount = (amount: string | number | undefined): string => {
  if (!amount) return '0';
  
  // Convert to number if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Return formatted number with no decimal places and thousand separators
  return Math.floor(numericAmount).toLocaleString('en-US');
};

/**
 * Formats dates optimized for South Africa region
 * Uses South African locale (en-ZA) with day/month/year format
 * Examples: "15 Dec 2024", "03 Jan 2025"
 */
export const formatDateForSouthAfrica = (date: string | Date | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    // Format using South African locale with custom options
    return dateObj.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '';
  }
};
